import { useEffect, useRef } from "react";

const DEFAULT_SUFFIX_KEYS = ["Enter"];

const isEditableTarget = (target) => {
    if (!(target instanceof HTMLElement)) return false;

    return (
        target.isContentEditable ||
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
    );
};

const isModifierKey = (key) => {
    return [
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "CapsLock",
        "NumLock",
        "Fn",
    ].includes(key);
};

const dispatchBarcodeEvent = (barcode, source = "keyboard-wedge") => {
    const event = new Event("barcode");
    event.value = barcode;
    event.source = source;
    window.dispatchEvent(event);
};

/**
 * Invisible scanner listener.
 *
 * It captures fast keyboard-wedge input from scanners configured as keyboard output.
 * It does not render an input and does not require focus.
 */
export default function KeyboardWedgeScanner({
    enabled,
    onBarcode,
    suffixKeys = DEFAULT_SUFFIX_KEYS,
    minLength = 8,
    maxInterKeyDelayMs = 60,
    allowTimeoutSubmit = false,
    preventDefault = false,
    ignoreEditableTargets = true,
    dedupeWindowMs = 400,
}) {
    const bufferRef = useRef("");
    const firstKeyAtRef = useRef(0);
    const lastKeyAtRef = useRef(0);
    const timeoutRef = useRef(null);

    // Used to avoid dispatching the keyboard version when Android intent already emitted it.
    const lastBarcodeEventRef = useRef({
        value: null,
        timestamp: 0,
    });

    useEffect(() => {
        const onExistingBarcodeEvent = (event) => {
            if (!event?.value) return;

            lastBarcodeEventRef.current = {
                value: String(event.value),
                timestamp: performance.now(),
            };
        };

        window.addEventListener("barcode", onExistingBarcodeEvent, true);

        return () => {
            window.removeEventListener("barcode", onExistingBarcodeEvent, true);
        };
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const reset = () => {
            bufferRef.current = "";
            firstKeyAtRef.current = 0;
            lastKeyAtRef.current = 0;

            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const submit = (completedBy) => {
            const barcode = bufferRef.current;
            const firstKeyAt = firstKeyAtRef.current;
            const lastKeyAt = lastKeyAtRef.current;

            if (!barcode || barcode.length < minLength || !firstKeyAt || !lastKeyAt) {
                reset();
                return false;
            }

            const durationMs = lastKeyAt - firstKeyAt;
            const maxExpectedDurationMs = Math.max(
                100,
                (barcode.length - 1) * maxInterKeyDelayMs,
            );

            const looksLikeScannerInput = durationMs <= maxExpectedDurationMs;

            reset();

            if (!looksLikeScannerInput) return false;

            const lastBarcodeEvent = lastBarcodeEventRef.current;
            const isDuplicateOfRecentAndroidIntent =
                lastBarcodeEvent.value === barcode &&
                performance.now() - lastBarcodeEvent.timestamp <= dedupeWindowMs;

            if (isDuplicateOfRecentAndroidIntent) {
                return true;
            }

            if (onBarcode) {
                onBarcode({
                    data: barcode,
                    source: "keyboard-wedge",
                    completedBy,
                    durationMs,
                });
            } else {
                dispatchBarcodeEvent(barcode);
            }

            return true;
        };

        const scheduleTimeoutSubmit = () => {
            if (!allowTimeoutSubmit) return;

            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = window.setTimeout(() => {
                submit("timeout");
            }, maxInterKeyDelayMs + 30);
        };

        const onKeyDown = (event) => {
            if (event.isComposing) return;

            if (ignoreEditableTargets && isEditableTarget(event.target)) {
                return;
            }

            const now = performance.now();

            if (suffixKeys.includes(event.key)) {
                const accepted = submit("suffix");

                if (accepted && preventDefault) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                return;
            }

            if (isModifierKey(event.key)) return;

            if (
                event.key.length !== 1 ||
                event.ctrlKey ||
                event.metaKey ||
                event.altKey
            ) {
                reset();
                return;
            }

            const gap = now - lastKeyAtRef.current;

            if (bufferRef.current && gap > maxInterKeyDelayMs) {
                reset();
            }

            if (!bufferRef.current) {
                firstKeyAtRef.current = now;
            }

            bufferRef.current += event.key;
            lastKeyAtRef.current = now;

            if (preventDefault) {
                event.preventDefault();
                event.stopPropagation();
            }

            scheduleTimeoutSubmit();
        };

        window.addEventListener("keydown", onKeyDown, true);

        return () => {
            window.removeEventListener("keydown", onKeyDown, true);
            reset();
        };
    }, [
        enabled,
        onBarcode,
        suffixKeys,
        minLength,
        maxInterKeyDelayMs,
        allowTimeoutSubmit,
        preventDefault,
        ignoreEditableTargets,
        dedupeWindowMs,
    ]);

    return null;
}
